import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/components/auth/AuthProvider";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";

interface ReviewFormProps {
  productId: string;
}

const ReviewForm = ({ productId }: ReviewFormProps) => {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [location, setLocation] = useState("");
  const { user } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Check if user has already reviewed
  const { data: existingReview, isLoading: checkingReview } = useQuery({
    queryKey: ['user-review', productId, user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data } = await supabase
        .from('reviews')
        .select('rating')
        .eq('product_id', productId)
        .eq('user_id', user.id)
        .maybeSingle();
      
      return data;
    },
    enabled: !!user?.id
  });

  const updateProductRating = async () => {
    console.log('Updating product rating...');
    
    // Get all reviews for the product
    const { data: reviews } = await supabase
      .from('reviews')
      .select('rating')
      .eq('product_id', productId);

    if (!reviews) return;

    // Calculate new average rating
    const totalRating = reviews.reduce((sum, review) => sum + (review.rating || 0), rating);
    const newAverageRating = totalRating / (reviews.length + 1);
    console.log('New average rating:', newAverageRating);

    // Update product rating
    const { error: updateError } = await supabase
      .from('products')
      .update({ 
        rating: newAverageRating,
        reviews_count: reviews.length + 1 
      })
      .eq('id', productId);

    if (updateError) {
      console.error('Error updating product rating:', updateError);
      throw updateError;
    }
  };

  const reviewMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error('User not authenticated');
      
      // First insert the review
      const { error: reviewError } = await supabase
        .from('reviews')
        .insert({
          product_id: productId,
          user_id: user.id,
          rating,
          comment,
          purchase_location: location
        });
        
      if (reviewError) throw reviewError;

      // Then update the product's average rating
      await updateProductRating();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product', productId] });
      queryClient.invalidateQueries({ queryKey: ['user-review', productId, user?.id] });
      toast({
        title: t('products.reviewSubmitted'),
        description: t('products.reviewSubmittedSuccess'),
      });
      setComment("");
      setLocation("");
      setRating(5);
    },
    onError: (error) => {
      console.error('Error submitting review:', error);
      toast({
        title: t('errors.error'),
        description: t('errors.reviewSubmissionFailed'),
        variant: "destructive"
      });
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      toast({
        title: t('auth.required'),
        description: t('auth.pleaseLoginFirst'),
      });
      return;
    }
    reviewMutation.mutate();
  };

  if (checkingReview) return null;

  if (existingReview) {
    return (
      <div className="mt-6 p-4 bg-muted rounded-lg">
        <p className="text-sm text-muted-foreground">
          {t('products.alreadyReviewed')}
        </p>
        <p className="text-sm">
          {t('products.yourRating')}: {existingReview.rating}/5
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 mt-6">
      <h3 className="font-semibold text-lg">{t('products.writeReview')}</h3>
      
      <div>
        <label className="block text-sm font-medium mb-1">
          {t('products.rating')} (1-5)
        </label>
        <Input
          type="number"
          min="1"
          max="5"
          value={rating}
          onChange={(e) => setRating(Number(e.target.value))}
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">
          {t('products.comment')}
        </label>
        <Textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          required
          placeholder={t('products.shareExperience')}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">
          {t('products.purchaseLocation')}
        </label>
        <Input
          type="text"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder={t('products.whereBought')}
        />
      </div>

      <Button 
        type="submit" 
        disabled={reviewMutation.isPending}
      >
        {t('products.submitReview')}
      </Button>
    </form>
  );
};

export default ReviewForm;